import React, { useRef, useEffect, useCallback } from 'react';
import DragHandleMenu from './DragHandleMenu';
import ConnectionMenu from './ConnectionMenu';
import { useNodeResize, useConnectionMenu, useTaskNodeHandlers, useTaskNodeStyles, MIN_WIDTH, MIN_HEIGHT } from './hooks';
import { useIconPicker } from '../../hooks/useIconPicker';
import { useArtifacts } from './hooks/useArtifacts';
import ConnectionHandle from './components/ConnectionHandle';
import ResizeHandles from './components/ResizeHandles';
import { MissionCardBody } from '../../shared/CardComponents';
import IconPicker from '../../shared/IconPicker';

/**
 * TaskNode Component
 * 
 * Flow view wrapper for MissionCardBody.
 * Handles view-specific concerns: positioning, connection handles,
 * resize handles, drag handle menu, inputs/deliverables popovers.
 */
const TaskNode = ({
  task,
  position,
  isSelected,
  isDragging,
  campaignId,
  layoutSurfaceEpoch: _layoutSurfaceEpoch = 0,
  isConnecting,
  isConnectionTarget,
  isConnectionHovered,
  isFocusedMain,
  isFocusDependency,
  isAnimating,
  isFadingOut,
  isFadingIn,
  isDropTarget,
  lockState,
  statuses,
  allCategories = [],
  allTags = [],
  allTasks = [],
  nestedSprint = null,
  nestedSprints = [],
  allSprints = [],
  zoom = 1,
  onMouseDown,
  onClick,
  onConnectionDragStart,
  onCreateConnectedNode,
  checkClipboard,
  onCreateCampaign,
  onStatusChange,
  onCategoryChange,
  onAddTag,
  onRemoveTag,
  onSetPrimaryTag,
  onIconChange,
  onIconDeleted,
  onDelete,
  onCopy,
  onDuplicate,
  onCreateParallelMission,
  onFocusTask,
  onRename,
  onDimensionsChange,
  onResize,
  onResizeEnd,
  onResetSize,
  onOpenNestedSprint,
  onCreateNestedSprint,
  onRemoveNestedSprint,
  onDeliverablesChange,
  onInputsChange,
  onGoToTask,
  onOpenDetails,
  onOpenAttachments,
  onAddStatus,
  onDeleteStatus,
  onRenameStatus,
  onSetStatusAutoSync,
  onRenameCategory,
  onDeleteCategory,
  onRenameTag,
  onDeleteTag,
  onDueDateChange,
  onRemindersChange,
  onEstimatedDurationMinutesChange,
  onCreatedAtChange,
  onStartedAtChange,
  onCompletedAtChange,
  onUpdatedAtChange,
  onPriorityChange,
  onDifficultyChange,
  onCustomFieldValueChange,
  customFields = [],
  onArchiveTask,
  onUnarchiveTask,
  onDependencyChange,
  onAddMilestoneOnHost,
  onAddNoteOnHost,
  onConditionsChange,
  onConditionsDialogOpen,
  onConditionsDialogClose,
  cardDisplaySettings = {},
  isTemplate = false,
  onQuickViewMouseEnter,
  onQuickViewMouseLeave,
  onQuickViewMouseMove,
  isHoveredFromHUD = false,
  onNodeHoverForHUD,
  onNodeLeaveForHUD,
  isMultiCampaignMode = false,
  currentView,
  customViews = [],
  activeCustomViewId = null,
  onViewChange,
  onSelectCustomView,
  onGoToTaskInView,
  onSubmitDeliverable,
  onEditDeliverables,
  onClearDeliverable,
  onNavigateToParentMission,
  onExplodeNestedCampaign,
}) => {
  const nodeRef = useRef(null);
  const dragHandleMenuRef = useRef(null);

  // Style computation
  const styles = useTaskNodeStyles({
    task,
    allCategories,
    allTags,
    nestedSprint,
    statuses,
    lockState,
    isSelected,
    isDragging,
    isResizing: false, // Updated below
    isConnecting,
    isConnectionTarget,
    isConnectionHovered,
    isFocusedMain,
    isFocusDependency,
    isAnimating,
    isFadingOut,
    isFadingIn,
    isDropTarget,
    isHoveredFromHUD,
    isMultiCampaignMode,
  });

  // Artifacts hook (inputs & deliverables)
  const artifacts = useArtifacts({
    task,
    allTasks,
    onDeliverablesChange,
    onInputsChange,
  });

  // Custom hooks
  const { isResizing, handleResizeMouseDown } = useNodeResize({
    nodeRef,
    taskId: task.id,
    onResize,
    onResizeEnd,
    onResetSize,
    minWidth: MIN_WIDTH,
    minHeight: MIN_HEIGHT,
    getZoom: () => zoom,
    getPosition: () => position,
  });

  const connectionMenu = useConnectionMenu({
    taskId: task.id,
    onCreateConnectedNode,
  });

  const iconPicker = useIconPicker({
    taskId: task.id,
    onIconChange,
  });

  const handlers = useTaskNodeHandlers({
    task,
    onMouseDown,
    onConnectionDragStart,
    connectionMenu,
  });

  // Listen for external requests to open icon picker
  useEffect(() => {
    const handleOpenIconPicker = (e) => {
      if (e.detail.taskId === task.id) {
        iconPicker.openPicker(null, e.detail.position);
      }
    };
    window.addEventListener('openTaskIconPicker', handleOpenIconPicker);
    return () => window.removeEventListener('openTaskIconPicker', handleOpenIconPicker);
  }, [task.id, iconPicker]);

  // Listen for external requests to open drag handle menu (from HandlesLayer DragHandle)
  useEffect(() => {
    const handleOpenDragMenu = (e) => {
      if (e.detail.taskId === task.id && dragHandleMenuRef.current) {
        const { handleRect } = e.detail;
        // Position menu to the left of the handle, similar to how right-click works
        const viewportHeight = window.innerHeight;
        const menuHeight = 350;
        
        let top = handleRect.top;
        if (top + menuHeight > viewportHeight) {
          top = viewportHeight - menuHeight - 10;
        }
        
        // Position menu to the left of the handle
        let left = handleRect.left - 180;
        if (left < 10) {
          left = handleRect.right + 8;
        }
        
        dragHandleMenuRef.current.openAtPosition(left, top);
      }
    };
    window.addEventListener('openTaskDragMenu', handleOpenDragMenu);
    return () => window.removeEventListener('openTaskDragMenu', handleOpenDragMenu);
  }, [task.id]);

  // Report dimensions when they change
  // IMPORTANT: Debounce the ResizeObserver callback to prevent layout thrashing.
  // Without debouncing, transient layout states during re-renders (e.g., when
  // selection changes) can trigger dimension updates that cascade into
  // position adjustments via useVerticalSpacingOnResize.
  useEffect(() => {
    if (nodeRef.current && onDimensionsChange) {
      const node = nodeRef.current;
      let resizeTimer = null;
      
      const updateDimensions = () => {
        if (!nodeRef.current) return;
        const width = nodeRef.current.offsetWidth;
        const height = nodeRef.current.offsetHeight;
        onDimensionsChange(task.id, { width, height });
      };
      
      // Initial measurement (immediate)
      updateDimensions();
      
      // Debounced handler for subsequent resizes
      const debouncedUpdate = () => {
        if (resizeTimer) clearTimeout(resizeTimer);
        resizeTimer = setTimeout(updateDimensions, 50);
      };
      
      const observer = new ResizeObserver(debouncedUpdate);
      observer.observe(node);
      
      return () => {
        if (resizeTimer) clearTimeout(resizeTimer);
        observer.disconnect();
      };
    }
  }, [task.id, onDimensionsChange]);


  // Update classes with isResizing state
  const nodeClasses = styles.nodeClasses + (isResizing ? ' task-node--resizing' : '');

  // Track right-click start position to distinguish actual right-clicks from drag-releases
  const rightClickStartRef = useRef(null);

  // Wrapper for mouse down that tracks right-click start position
  const handleNodeMouseDownWrapper = useCallback((e) => {
    // Track right-click start position
    if (e.button === 2) {
      rightClickStartRef.current = { x: e.clientX, y: e.clientY, nodeId: task.id };
    }
    // Call the original handler from useTaskNodeHandlers
    handlers.handleNodeMouseDown(e);
  }, [handlers, task.id]);

  // Handle right-click to open context menu
  const handleContextMenu = useCallback((e) => {
    // Only open menu if the right-click started on THIS node (not a drag from outside)
    if (!rightClickStartRef.current || rightClickStartRef.current.nodeId !== task.id) {
      rightClickStartRef.current = null;
      return;
    }
    
    // Check if it was a drag (moved more than a few pixels)
    const deltaX = Math.abs(e.clientX - rightClickStartRef.current.x);
    const deltaY = Math.abs(e.clientY - rightClickStartRef.current.y);
    if (deltaX > 5 || deltaY > 5) {
      // It was a drag, not a click - don't open menu
      rightClickStartRef.current = null;
      return;
    }
    
    rightClickStartRef.current = null;
    
    // Check if the click target is an interactive element
    const target = e.target;
    const isInteractive = target.closest('button, input, select, textarea, [role="button"], .status-dropdown, .priority-dropdown, .difficulty-dropdown, .custom-field-dropdown, .task-node__tags-btn, .task-node__drag-handle, .task-node__drag-zone');
    
    if (!isInteractive && dragHandleMenuRef.current) {
      e.preventDefault();
      e.stopPropagation();
      dragHandleMenuRef.current.openAtPosition(e.clientX, e.clientY);
    }
  }, [task.id]);

  return (
    <div
      ref={nodeRef}
      className={nodeClasses}
      data-task-id={task.id}
      style={{
        '--node-tx': `${position.x}px`,
        '--node-ty': `${position.y}px`,
        width: styles.nodeWidth,
        minHeight: task.height ? styles.nodeHeight : MIN_HEIGHT,
        ...styles.nodeStyles,
      }}
      onMouseDown={handleNodeMouseDownWrapper}
      onClick={onClick}
      onContextMenu={handleContextMenu}
      onMouseEnter={(e) => {
        onQuickViewMouseEnter?.(e);
        onNodeHoverForHUD?.();
      }}
      onMouseLeave={(e) => {
        onQuickViewMouseLeave?.(e);
        onNodeLeaveForHUD?.();
      }}
      onMouseMove={onQuickViewMouseMove}
    >
      {/* Connection handle (bottom) */}
      <ConnectionHandle
        ref={connectionMenu.handleRef}
        onMouseDown={handlers.handleConnectionHandleMouseDown}
        onClick={handlers.handleConnectionHandleClick}
      />

      {/* Connection menu (portal) */}
      {connectionMenu.showMenu && connectionMenu.menuPosition && (
        <ConnectionMenu
          position={connectionMenu.menuPosition}
          onSelect={connectionMenu.selectNodeType}
          onClose={connectionMenu.closeMenu}
          isMultiCampaignMode={isMultiCampaignMode}
          sourceCampaign={styles.sourceCampaign}
          checkClipboard={checkClipboard}
          onCreateCampaign={onCreateCampaign
            ? () => onCreateCampaign({ sourceTaskId: task.id, mode: 'after' })
            : undefined}
        />
      )}

      {/* Drag handle with menu */}
      <DragHandleMenu
        ref={dragHandleMenuRef}
        task={task}
        onDelete={onDelete}
        onCopy={onCopy}
        onDuplicate={onDuplicate}
        onCreateParallelMission={onCreateParallelMission}
        onFocusTask={onFocusTask}
        hasNestedSprint={!!nestedSprint}
        nestedSprint={nestedSprint}
        onOpenNestedSprint={() => onOpenNestedSprint?.(nestedSprint)}
        onCreateNestedSprint={() => onCreateNestedSprint?.(task.id)}
        hasIcon={!!task.icon}
        onAddIcon={iconPicker.openPicker}
        onOpenDetails={() => onOpenDetails?.(task.id)}
        onOpenAttachments={() => onOpenAttachments?.(task.id)}
        onArchive={() => onArchiveTask?.(task.id)}
        onUnarchive={() => onUnarchiveTask?.(task.id)}
        currentView={currentView}
        customViews={customViews}
        activeCustomViewId={activeCustomViewId}
        onViewChange={onViewChange}
        onSelectCustomView={onSelectCustomView}
        onGoToTaskInView={onGoToTaskInView}
        onExplodeNestedCampaign={onExplodeNestedCampaign}
        onAddMilestoneOnHost={onAddMilestoneOnHost}
        onAddNoteOnHost={onAddNoteOnHost}
      />

      {/* Shared Card Body */}
      <MissionCardBody
        task={task}
        category={styles.currentCategory}
        allTags={styles.availableTags}
        statuses={statuses}
        nestedSprint={nestedSprint}
        nestedSprints={nestedSprints}
        allSprints={allSprints}
        isBlocked={styles.isBlocked}
        isLocked={styles.isLocked}
        isExcluded={styles.isExcluded}
        lockReason={lockState?.lockReason}
        blockingDependencies={lockState?.blockingTasks || []}
        parentMissionName={lockState?.parentMissionName}
        parentCampaignName={lockState?.parentCampaignName}
        parentMissionId={lockState?.parentMissionId}
        parentSprintId={lockState?.parentSprintId}
        onNavigateToParentMission={onNavigateToParentMission}
        cardDisplaySettings={cardDisplaySettings}
        isTemplate={isTemplate}
        onRename={onRename}
        onIconChange={onIconChange}
        onStatusChange={onStatusChange}
        onAddTag={onAddTag || onCategoryChange}
        onRemoveTag={onRemoveTag}
        onSetPrimaryTag={onSetPrimaryTag}
        onRenameTag={onRenameTag || onRenameCategory}
        onDeleteTag={onDeleteTag || onDeleteCategory}
        onOpenDetails={onOpenDetails}
        onOpenAttachments={onOpenAttachments}
        onOpenNestedSprint={onOpenNestedSprint}
        onRemoveNestedSprint={onRemoveNestedSprint}
        onAddStatus={onAddStatus}
        onDeleteStatus={onDeleteStatus}
        onRenameStatus={onRenameStatus}
        onSetStatusAutoSync={onSetStatusAutoSync}
        onDueDateChange={onDueDateChange}
        onRemindersChange={onRemindersChange}
        onEstimatedDurationMinutesChange={onEstimatedDurationMinutesChange}
        onCreatedAtChange={onCreatedAtChange}
        onStartedAtChange={onStartedAtChange}
        onCompletedAtChange={onCompletedAtChange}
        onUpdatedAtChange={onUpdatedAtChange}
        onPriorityChange={onPriorityChange}
        onDifficultyChange={onDifficultyChange}
        onCustomFieldValueChange={onCustomFieldValueChange}
        customFields={customFields}
        onArchiveTask={onArchiveTask}
        onUnarchiveTask={onUnarchiveTask}
        onDeleteTask={onDelete}
        onDependencyChange={onDependencyChange}
        onConditionsChange={onConditionsChange}
        onConditionsDialogOpen={onConditionsDialogOpen}
        onConditionsDialogClose={onConditionsDialogClose}
        allTasks={allTasks}
        onGoToDependency={onGoToTask}
        onSubmitDeliverable={onSubmitDeliverable}
        onEditDeliverables={onEditDeliverables}
        onClearDeliverable={onClearDeliverable}
        currentView={currentView}
        customViews={customViews}
        activeCustomViewId={activeCustomViewId}
        onViewChange={onViewChange}
        onSelectCustomView={onSelectCustomView}
        onGoToTaskInView={onGoToTaskInView}
      />

      {/* Resize handles */}
      <ResizeHandles onResizeMouseDown={handleResizeMouseDown} />

      {/* Icon Picker */}
      {iconPicker.showPicker && (
        <IconPicker
          currentIcon={task.icon}
          onIconChange={iconPicker.changeIcon}
          onIconDeleted={() => iconPicker.changeIcon(null)}
          onClose={iconPicker.closePicker}
          position={iconPicker.pickerPosition}
          nodeColor={styles.primaryTagObject?.color}
        />
      )}
    </div>
  );
};

export default TaskNode;
